import React from 'react';
import { useTheme } from '@lifeomic/chroma-react/styles';
import ReactDiffViewer from '@lifeomic/react-diff-viewer';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { Checkbox } from '@lifeomic/chroma-react/components/Checkbox';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { useLoadReferencedResources } from './useLoadReferencedResources';
import JsonViewer from './JsonViewer';

const messages = defineMessages({
  noDataLabel: {
    id: 'pages.auditLog.noDataLabel',
    defaultMessage: 'Could not find data referenced in this audit event.',
  },
  resourceVersionLabel: {
    id: 'pages.auditLog.resourceVersionLabel',
    defaultMessage: 'Version {version}: {label}',
  },
  diffLabel: {
    id: 'pages.auditLog.diffLabel',
    defaultMessage: 'Diff View',
  },
});

const sort = (object: any) => {
  if (typeof object !== 'object' || object instanceof Array) {
    return object;
  }
  const keys = Object.keys(object).sort();
  const newObject = {};
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  keys.forEach(_ => (newObject[_] = sort(object[_])));
  return newObject;
};

interface AuditEventModalProps {
  focusedEvent: fhir.AuditEvent | null;
  setFocusedEvent: (event: fhir.AuditEvent | null) => void;
}

const AuditEventModal: React.FC<AuditEventModalProps> = ({
  focusedEvent,
  setFocusedEvent,
}) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [checked, setChecked] = React.useState(false);
  const { newResource, prevResource, isLoading, error } =
    useLoadReferencedResources(focusedEvent);
  const entityArray = focusedEvent?.entity;

  const getVersionMessage = (entity: fhir.AuditEventEntity) => {
    return entity
      ? formatMessage(messages.resourceVersionLabel, {
          version: entity.lifecycle?.code,
          label: entity.lifecycle?.display,
        })
      : '';
  };
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const prevResourceLabel = getVersionMessage(entityArray?.[0]);
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const newResourceLabel = getVersionMessage(entityArray?.[1]);

  let content;
  if (error) content = <Text>{formatMessage(messages.noDataLabel)}</Text>;
  if (isLoading)
    content = (
      <Box fullWidth fullHeight justify="center" align="center">
        <DotLoader />
      </Box>
    );
  if (prevResource && newResource)
    content = (
      <div>
        <Box justify="flex-end" padding={1}>
          <Checkbox
            checked={checked}
            onChange={_ => {
              setChecked(_.target.checked);
            }}
            label={formatMessage(messages.diffLabel)}
          />
        </Box>
        {checked ? (
          <Paper fullWidth>
            <ReactDiffViewer
              oldValue={JSON.stringify(sort(prevResource), null, 4)}
              newValue={JSON.stringify(sort(newResource), null, 4)}
              leftTitle={prevResourceLabel}
              rightTitle={newResourceLabel}
              splitView={true}
              styles={{
                titleBlock: { fontFamily: theme.typography.fontFamily },
                contentText: { fontFamily: theme.typography.fontFamily },
              }}
            />
          </Paper>
        ) : (
          <Box>
            <JsonViewer resource={prevResource} label={prevResourceLabel} />
            <JsonViewer resource={newResource} label={newResourceLabel} />
          </Box>
        )}
      </div>
    );
  else if (newResource)
    content = <JsonViewer resource={newResource} label={prevResourceLabel} />;

  return (
    <Modal
      title={`${focusedEvent?.type?.display} | ${focusedEvent?.subtype
        ?.map(_ => _.display)
        .filter(_ => !!_)
        .join(', ')}`}
      isOpen={!!focusedEvent}
      isFullScreen={true}
      onDismiss={() => {
        setFocusedEvent(null);
      }}
    >
      {content ? content : <Text>{formatMessage(messages.noDataLabel)}</Text>}
    </Modal>
  );
};

export default React.memo(AuditEventModal);
